import React, { useEffect, useState } from 'react';
import { Palette, Save, Loader2, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const FONT_PRESETS = [
  { label: 'Sistema (Inter, Roboto…)', value: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
  { label: 'Sans serif moderno', value: '"Inter", system-ui, sans-serif' },
  { label: 'Serif clásico', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Sans serif elegante', value: '"Source Sans 3", "Helvetica Neue", sans-serif' },
  { label: 'Editorial', value: '"Playfair Display", Georgia, serif' },
];

const DEFAULTS = {
  primary_color: '#0e539d',
  accent_color: '#f59e0b',
  heading_font: 'system-ui, -apple-system, sans-serif',
  body_font: 'system-ui, -apple-system, sans-serif',
};

export default function AdminThemePage() {
  const [theme, setTheme] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    api.get('/site-theme').then(({ data }) => setTheme({ ...DEFAULTS, ...(data || {}) })).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 3500);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  async function save() {
    setSaving(true);
    try {
      await api.put('/admin/site-theme', theme);
      // Fire global event so SiteThemeProvider re-fetches and applies live.
      try { window.dispatchEvent(new Event('cmic:theme-updated')); } catch (_) {}
      setFeedback({ type: 'success', message: 'Tema actualizado. Los cambios ya están activos en el sitio público.' });
    } catch (err) {
      setFeedback({ type: 'error', message: formatApiError(err.response?.data?.detail) });
    } finally { setSaving(false); }
  }

  function reset() {
    setTheme(DEFAULTS);
  }

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>;

  return (
    <div data-testid="admin-theme-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
          <Palette size={22} className="text-cmic-600" />
          Tema visual del sitio
        </h1>
        <p className="text-charcoal-500 mt-1 text-sm">Colores y tipografías globales que se aplican en TODAS las páginas públicas.</p>
      </div>

      {feedback && (
        <div data-testid="admin-theme-feedback" className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-6 text-sm font-medium ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {feedback.message}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-2">Color primario</label>
            <div className="flex items-center gap-3">
              <input type="color" data-testid="theme-primary-input" value={theme.primary_color} onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })} className="w-14 h-12 rounded-lg border border-gray-200 cursor-pointer" />
              <input type="text" value={theme.primary_color} onChange={(e) => setTheme({ ...theme, primary_color: e.target.value })} className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-2">Color de acento</label>
            <div className="flex items-center gap-3">
              <input type="color" data-testid="theme-accent-input" value={theme.accent_color} onChange={(e) => setTheme({ ...theme, accent_color: e.target.value })} className="w-14 h-12 rounded-lg border border-gray-200 cursor-pointer" />
              <input type="text" value={theme.accent_color} onChange={(e) => setTheme({ ...theme, accent_color: e.target.value })} className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-2">Fuente de títulos</label>
          <select data-testid="theme-heading-font-select" value={theme.heading_font} onChange={(e) => setTheme({ ...theme, heading_font: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
            {FONT_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-2">Fuente del cuerpo</label>
          <select data-testid="theme-body-font-select" value={theme.body_font} onChange={(e) => setTheme({ ...theme, body_font: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
            {FONT_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        <div className="border-t border-gray-100 pt-5">
          <p className="text-xs font-semibold text-charcoal-600 mb-2 uppercase tracking-wider">Vista previa</p>
          <div className="rounded-xl p-5 border border-gray-200" style={{ fontFamily: theme.body_font }}>
            <h2 style={{ color: theme.primary_color, fontFamily: theme.heading_font }} className="text-2xl font-bold mb-2">Título de ejemplo CMIC</h2>
            <p className="text-charcoal-600 text-sm mb-3">Texto del cuerpo en la tipografía seleccionada. Esto es lo que se verá en las páginas públicas.</p>
            <button type="button" style={{ backgroundColor: theme.primary_color }} className="text-white px-4 py-2 rounded-lg text-sm font-semibold">
              Botón primario
            </button>
            <button type="button" style={{ backgroundColor: theme.accent_color }} className="ml-2 text-white px-4 py-2 rounded-lg text-sm font-semibold">
              Botón acento
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <button type="button" onClick={reset} className="inline-flex items-center gap-2 text-xs font-medium text-charcoal-500 hover:text-charcoal-700">
            <RotateCcw size={14} /> Restablecer
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="theme-save-btn" className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar tema
          </button>
        </div>
      </div>
    </div>
  );
}
